import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { SurfaceSourceRegion } from '@cornerstone/shared';
import { useCalibrationV2Store } from '../store/calibrationV2';
import { cn } from '../utils/cn';

const FULL_REGION: SurfaceSourceRegion = { x: 0, y: 0, width: 1, height: 1 };

export function SourceMapV2({
  mediaUrl,
  mediaType,
}: {
  mediaUrl: string | null;
  mediaType: 'image' | 'video' | null;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    region: SurfaceSourceRegion;
  } | null>(null);
  const surfaces = useCalibrationV2Store((s) => s.surfaces);
  const activeSurfaceId = useCalibrationV2Store((s) => s.activeSurfaceId);
  const setActiveSurface = useCalibrationV2Store((s) => s.setActiveSurface);
  const setSourceRegion = useCalibrationV2Store((s) => s.setSourceRegion);
  const beginDrag = useCalibrationV2Store((s) => s.beginDrag);
  const endDrag = useCalibrationV2Store((s) => s.endDrag);

  const startDrag = (
    event: ReactPointerEvent<HTMLDivElement>,
    surfaceId: string,
    mode: 'move' | 'resize',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveSurface(surfaceId);
    const surface = surfaces.find((item) => item.id === surfaceId);
    if (!surface) return;
    dragRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      region: surface.sourceRegion ?? FULL_REGION,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    beginDrag();
  };

  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const rect = mapRef.current?.getBoundingClientRect();
    if (!drag || !rect) return;
    const dx = (event.clientX - drag.startX) / rect.width;
    const dy = (event.clientY - drag.startY) / rect.height;
    setSourceRegion(
      drag.mode === 'move'
        ? { ...drag.region, x: drag.region.x + dx, y: drag.region.y + dy }
        : {
            ...drag.region,
            width: drag.region.width + dx,
            height: drag.region.height + dy,
          },
    );
  };

  const stopDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    endDrag();
  };

  return (
    <div
      ref={mapRef}
      className="relative aspect-video overflow-hidden rounded border border-line bg-bg"
    >
      {mediaUrl && mediaType === 'image' && (
        <img src={mediaUrl} alt="" className="absolute inset-0 h-full w-full object-contain" />
      )}
      {mediaUrl && mediaType === 'video' && (
        <video
          src={mediaUrl}
          muted
          loop
          autoPlay
          playsInline
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      {!mediaUrl && (
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#20242b_25%,transparent_25%),linear-gradient(-45deg,#20242b_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#20242b_75%),linear-gradient(-45deg,transparent_75%,#20242b_75%)] bg-[length:24px_24px] bg-[position:0_0,0_12px,12px_-12px,-12px_0]" />
      )}

      {surfaces.map((surface, index) => {
        const region = surface.sourceRegion ?? FULL_REGION;
        const active = surface.id === activeSurfaceId;
        const color = surface.color ?? '#38BDF8';
        return (
          <div
            key={surface.id}
            className={cn(
              'absolute cursor-move border-2 touch-none',
              active ? 'z-20' : 'z-10 opacity-70',
            )}
            style={{
              left: `${region.x * 100}%`,
              top: `${region.y * 100}%`,
              width: `${region.width * 100}%`,
              height: `${region.height * 100}%`,
              borderColor: color,
              backgroundColor: `${color}24`,
            }}
            onPointerDown={(event) => startDrag(event, surface.id, 'move')}
            onPointerMove={moveDrag}
            onPointerUp={stopDrag}
            onPointerCancel={stopDrag}
          >
            <span
              className="absolute left-1 top-1 rounded px-1 font-mono text-[10px] font-bold text-white"
              style={{ backgroundColor: color }}
            >
              S{index + 1}
            </span>
            {active && (
              <div
                className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize rounded-full border border-white"
                style={{ backgroundColor: color }}
                onPointerDown={(event) => startDrag(event, surface.id, 'resize')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
